<!-- The app's icons, drawn.
     ONE COMPONENT, ONE GRID, NO CHARACTERS. Every mark in this app that is
     not a letter comes from here.

     The rule this exists to enforce: an icon is never an emoji and never a
     dingbat glyph — not '▶', not '❚❚', not '✕', not '···'. A character is
     drawn by whatever font the device happened to fall back to, at whatever
     weight and baseline that font chose, and it recolours, aligns and scales
     differently on every phone. Two of them side by side never look like a
     set. The prototype for this redesign shipped with unicode play and pause
     marks and the first thing anyone said about it was that it looked cheap;
     they were right, and this file is the fix.

     Everything below is a 24×24 viewBox. Strokes are 1.7 (1.9 where a mark
     has to hold at 15px or under), round-capped and round-joined; solids are
     used only where the shape IS the mark — the play triangle, the record
     dot, the stop square, the menu dots — because a hollow play triangle
     reads as an outline of a button rather than as the button.

     Size defaults to 20 and is passed as a NUMBER of CSS pixels, so a caller
     can line an icon up with a 28px ruled line by arithmetic instead of by
     eye. Colour is always currentColor: the icon inherits from the control it
     sits in, which is what makes one file work on paper, on the dark cook bar
     and on a red disc. -->
<script setup>
import { computed } from 'vue';

/* Two kinds of mark. STROKE icons are line drawings — the pencil, the trash,
   the chevrons — and share one weight so they read as one hand. SOLID icons
   are silhouettes, used where an outline would be wrong. Keeping them in one
   table rather than one component each is what stops the set drifting: every
   new icon has to pick a side and inherit the geometry. */
const STROKE = {
  'chevron-left': ['M15 18l-6-6 6-6'],
  'chevron-right': ['M9 18l6-6-6-6'],
  'chevron-down': ['M6 9l6 6 6-6'],
  plus: ['M12 5v14', 'M5 12h14'],
  check: ['M20 6L9 17l-5-5'],
  close: ['M6 6l12 12', 'M18 6L6 18'],
  pencil: ['M12 20h9', 'M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z'],
  trash: ['M4 7h16', 'M10 11v6', 'M14 11v6', 'M6 7l1 13h10l1-13', 'M9 7V4h6v3'],
  copy: ['M9 9h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-8a2 2 0 0 1-2-2V9Z',
         'M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1'],
  share: ['M12 15V3', 'M8 7l4-4 4 4', 'M4 13v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6'],
  print: ['M6 9V3h12v6', 'M5 9h14a2 2 0 0 1 2 2v5H3v-5a2 2 0 0 1 2-2Z', 'M6 14h12v7H6z'],
  /* The microphone is the app's own noun — a recording that exists, as
     opposed to the red dot, which is a recording about to be made. */
  mic: ['M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z', 'M19 11v1a7 7 0 0 1-14 0v-1', 'M12 19v3'],
  /* A written note is not audio and must never wear the play triangle. */
  lines: ['M5 7h14', 'M5 12h14', 'M5 17h9'],
  book: ['M4 4.5A1.5 1.5 0 0 1 5.5 3H19v18H5.5A1.5 1.5 0 0 1 4 19.5v-15Z', 'M8 3v18'],
  gear: ['M19.4 15a1.6 1.6 0 0 0 .32 1.77l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-.97 1.47V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9.1 19.4a1.6 1.6 0 0 0-1.77.32l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.6 1.6 0 0 0 4.82 15a1.6 1.6 0 0 0-1.47-.97H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.32-1.77l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.6 1.6 0 0 0 9 4.82a1.6 1.6 0 0 0 .97-1.47V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 .97 1.47 1.6 1.6 0 0 0 1.77-.32l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.6 1.6 0 0 0 19.4 9v.03a1.6 1.6 0 0 0 1.47.97H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5.97Z'],
};

/* The circle in `gear` cannot be expressed as one of the paths above without
   losing its roundness to a bezier approximation, so a couple of icons carry
   an extra primitive. Kept as a separate table rather than as a special case
   inside the renderer. */
const STROKE_CIRCLES = {
  gear: [{ cx: 12, cy: 12, r: 3.2 }],
};

const SOLID = {
  play: ['M7 4.5v15a1 1 0 0 0 1.5.87l12-7.5a1 1 0 0 0 0-1.74l-12-7.5A1 1 0 0 0 7 4.5Z'],
  pause: ['M6 4h4.5v16H6z', 'M13.5 4H18v16h-4.5z'],
  stop: ['M5.6 5.6h12.8v12.8H5.6z'],
  record: ['M12 4.5a7.5 7.5 0 1 0 0 15 7.5 7.5 0 0 0 0-15Z'],
  dots: ['M12 3.25a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5Z',
         'M12 10.25a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5Z',
         'M12 17.25a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5Z'],
};

const props = defineProps({
  /** Which mark. An unknown name renders nothing rather than a broken box. */
  name: { type: String, required: true },
  /** CSS pixels, square. Pass a number so callers can do arithmetic with it. */
  size: { type: [Number, String], default: 20 },
  /** Stroke weight, for the few marks that have to hold at 15px or under. */
  weight: { type: [Number, String], default: 1.7 },
  /* An icon beside its own label is decoration and must be hidden from a
     screen reader — the label already says it. An icon that IS the control
     needs a name, and then the caller passes one. */
  label: { type: String, default: '' },
});

const solid = computed(() => Object.hasOwn(SOLID, props.name));
const paths = computed(() => (solid.value ? SOLID[props.name] : STROKE[props.name]) ?? []);
const circles = computed(() => STROKE_CIRCLES[props.name] ?? []);
const px = computed(() => `${Number(props.size) || 20}px`);
</script>

<template>
  <svg
    class="hd-icon"
    :width="px"
    :height="px"
    viewBox="0 0 24 24"
    :fill="solid ? 'currentColor' : 'none'"
    :stroke="solid ? 'none' : 'currentColor'"
    :stroke-width="solid ? undefined : weight"
    stroke-linecap="round"
    stroke-linejoin="round"
    :role="label ? 'img' : 'presentation'"
    :aria-label="label || undefined"
    :aria-hidden="label ? undefined : 'true'"
    focusable="false"
  >
    <circle v-for="(c, i) in circles" :key="`c${i}`" :cx="c.cx" :cy="c.cy" :r="c.r" />
    <path v-for="(d, i) in paths" :key="i" :d="d" />
  </svg>
</template>

<style scoped>
/* `block` so an icon in a flex row does not inherit the 4px descender gap a
   baseline-aligned inline element gets — the gap that quietly breaks a 28px
   ruled line into 32. */
.hd-icon { display: block; flex: none; }
</style>
